import type { BenLanguageCode } from '@ben/common/languages';
import { useIsMobile } from '@ben/orion/hooks/use-is-mobile';
import { useLeftNavigationTheme } from '@ben/orion/hooks/use-left-navigation-theme';
import { Fragment, type ReactNode } from 'react';
import styled, { css } from 'styled-components';
import type { EmployeeMenuEmployee } from '../employee-menu';
import EmployeeMenu from '../employee-menu';
import type { HelpMenuProps } from '../help-menu/help-menu';
import IconMenus from './icon-menus';

export interface EmployeeCardProps {
  employee: EmployeeMenuEmployee;
  onOpenPaySummary?: () => void;
  onSignOut: () => void;
  language?: BenLanguageCode;
  onChangeLanguage?: (value: BenLanguageCode) => void;
  notificationMenu?: ReactNode;
  showSwitchToAdminView?: boolean;
  showSwitchToEmployeeView?: boolean;
  helpMenuProps: HelpMenuProps;
  showLifeEvent?: boolean;
  showProfile?: boolean;
  onShowProfile?: () => void;
}

const EmployeeCard = ({
  employee,
  onSignOut,
  onOpenPaySummary,
  language,
  onChangeLanguage,
  notificationMenu,
  showSwitchToAdminView,
  showSwitchToEmployeeView,
  helpMenuProps,
  showLifeEvent,
  showProfile,
  onShowProfile,
}: EmployeeCardProps) => {
  const { isDarkBgColorVariant } = useLeftNavigationTheme();
  const isMobile = useIsMobile();
  const IconMenuWrapper =
    notificationMenu && !isMobile ? Fragment : RightAlignedContainer;

  return (
    <Card $isDarkBgColorVariant={isDarkBgColorVariant}>
      <EmployeeMenu
        data-testid="employee-menu"
        employee={employee}
        onSignOut={onSignOut}
        onOpenPaySummary={onOpenPaySummary}
        showSwitchToAdminView={showSwitchToAdminView}
        showSwitchToEmployeeView={showSwitchToEmployeeView}
        showLifeEvent={showLifeEvent}
        showProfile={showProfile}
        onShowProfile={onShowProfile}
      />
      <IconMenuWrapper>
        <IconMenus
          notificationMenu={notificationMenu}
          language={language}
          onChangeLanguage={onChangeLanguage}
          helpMenuProps={helpMenuProps}
        />
      </IconMenuWrapper>
    </Card>
  );
};

const Card = styled.div<{ $isDarkBgColorVariant: boolean }>`
  ${({ theme, $isDarkBgColorVariant }) => css`
    padding: ${theme.spacing(1)};
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    border-radius: ${theme.borderRadius.md};
    height: 58px;
    border: ${$isDarkBgColorVariant
      ? `1px solid ${theme.palette.white(0.15)}`
      : `1px solid ${theme.palette.black(0.15)}`};
  `}
`;

const RightAlignedContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacing(2)};
  `}
`;

export default EmployeeCard;
